import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/common/Sidebar";
import "../styles/Profile.css";
import { useAuth } from "../contexts/AuthContext";
import api from "../utils/api";

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showErrorReportModal, setShowErrorReportModal] = useState(false);
  const passwordFormRef = useRef<HTMLFormElement>(null);
  const errorReportFormRef = useRef<HTMLFormElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [errorReportSuccess, setErrorReportSuccess] = useState<string | null>(
    null
  );

  // 사이드바 메뉴 아이템
  const sidebarItems = [
    {
      id: "dashboard",
      label: "메인화면",
      icon: "home",
      onClick: () => navigate("/dashboard"),
    },
    {
      id: "profile",
      label: "프로필",
      icon: "user",
      onClick: () => navigate("/profile"),
    },
    {
      id: "error-report",
      label: "오류신고",
      icon: "alert",
      onClick: () => setShowErrorReportModal(true),
    },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleSubmitErrorReport = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setErrorReportSuccess(null);

    if (!errorReportFormRef.current) return;

    const formData = new FormData(errorReportFormRef.current);
    const content = formData.get("content") as string;

    // 내용이 비어있는지 검사
    if (!content.trim()) {
      setError("오류 내용을 입력해주세요.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.post("/reports", {
        content,
      });

      if (response.status === 200 || response.status === 201) {
        setErrorReportSuccess("오류가 성공적으로 신고되었습니다.");
        // 폼 초기화
        if (errorReportFormRef.current) {
          errorReportFormRef.current.reset();
        }

        // 3초 후 모달 닫기
        setTimeout(() => {
          setShowErrorReportModal(false);
          setErrorReportSuccess(null);
        }, 3000);
      }
    } catch (error) {
      setError("오류 신고에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitPasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!passwordFormRef.current) return;

    const formData = new FormData(passwordFormRef.current);
    const currentPassword = formData.get("currentPassword") as string;
    const newPassword = formData.get("newPassword") as string;
    const confirmPassword = formData.get("confirmPassword") as string;

    // 비밀번호 유효성 검사
    if (newPassword !== confirmPassword) {
      setError("새 비밀번호와 확인 비밀번호가 일치하지 않습니다.");
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.put("/auth/password", {
        currentPassword,
        newPassword,
      });

      if (response.status === 200) {
        setSuccess("비밀번호가 성공적으로 변경되었습니다.");
        // 폼 초기화
        if (passwordFormRef.current) {
          passwordFormRef.current.reset();
        }
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        setError(
          err.response.data.message || "비밀번호 변경 중 오류가 발생했습니다."
        );
      } else {
        setError("서버 오류가 발생했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 비밀번호 변경 모달
  const PasswordChangeModal = () => {
    // 비밀번호 일치 여부 확인을 위한 함수
    const checkPasswordsMatch = () => {
      if (!passwordFormRef.current) return true;

      const newPassword = passwordFormRef.current.newPassword?.value;
      const confirmPassword = passwordFormRef.current.confirmPassword?.value;

      if (newPassword && confirmPassword) {
        return newPassword === confirmPassword;
      }
      return true;
    };

    return (
      <div className="modal-backdrop">
        <div className="modal-content">
          <h2>비밀번호 변경</h2>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          <form ref={passwordFormRef} onSubmit={handleSubmitPasswordChange}>
            <div className="form-group">
              <label htmlFor="currentPassword">현재 비밀번호</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">새 비밀번호</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                required
                onChange={() => {
                  const match = checkPasswordsMatch();
                  const mismatchEl =
                    document.querySelector(".password-mismatch");
                  const matchEl = document.querySelector(".password-match");

                  if (mismatchEl && matchEl) {
                    if (
                      !match &&
                      passwordFormRef.current?.confirmPassword?.value
                    ) {
                      mismatchEl.classList.remove("hidden");
                      matchEl.classList.add("hidden");
                    } else if (
                      match &&
                      passwordFormRef.current?.confirmPassword?.value
                    ) {
                      mismatchEl.classList.add("hidden");
                      matchEl.classList.remove("hidden");
                    } else {
                      mismatchEl.classList.add("hidden");
                      matchEl.classList.add("hidden");
                    }
                  }
                }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">비밀번호 확인</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                required
                onChange={() => {
                  const match = checkPasswordsMatch();
                  const mismatchEl =
                    document.querySelector(".password-mismatch");
                  const matchEl = document.querySelector(".password-match");

                  if (mismatchEl && matchEl) {
                    if (
                      !match &&
                      passwordFormRef.current?.confirmPassword?.value
                    ) {
                      mismatchEl.classList.remove("hidden");
                      matchEl.classList.add("hidden");
                    } else if (
                      match &&
                      passwordFormRef.current?.confirmPassword?.value
                    ) {
                      mismatchEl.classList.add("hidden");
                      matchEl.classList.remove("hidden");
                    } else {
                      mismatchEl.classList.add("hidden");
                      matchEl.classList.add("hidden");
                    }
                  }
                }}
              />
              <div className="password-mismatch hidden">
                비밀번호가 일치하지 않습니다.
              </div>
              <div className="password-match hidden">
                비밀번호가 일치합니다.
              </div>
            </div>
            <div className="button-group">
              <button
                type="button"
                className="cancel-button"
                onClick={() => setShowPasswordModal(false)}
                disabled={isLoading}
              >
                취소
              </button>
              <button
                type="submit"
                className="save-button"
                disabled={isLoading}
              >
                {isLoading ? "처리 중..." : "변경하기"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // 오류 신고 모달
  const ErrorReportModal = () => (
    <div className="modal-backdrop">
      <div className="modal-content error-report-modal">
        <h2>오류 신고</h2>
        {error && <div className="error-message">{error}</div>}
        {errorReportSuccess && (
          <div className="success-message">{errorReportSuccess}</div>
        )}
        <form ref={errorReportFormRef} onSubmit={handleSubmitErrorReport}>
          <div className="form-group">
            <label htmlFor="errorContent">오류 내용</label>
            <textarea
              id="errorContent"
              name="content"
              required
              rows={8}
              placeholder="발생한 오류에 대해 자세히 설명해주세요."
            />
          </div>
          <div className="button-group">
            <button
              type="button"
              className="cancel-button"
              onClick={() => setShowErrorReportModal(false)}
              disabled={isLoading}
            >
              취소
            </button>
            <button type="submit" className="save-button" disabled={isLoading}>
              {isLoading ? "처리 중..." : "신고하기"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // 모달 열림/닫힘 시 body 스크롤 제어
  useEffect(() => {
    const hasOpenModal = showPasswordModal || showErrorReportModal;

    if (hasOpenModal) {
      // 모달이 열릴 때 현재 스크롤 위치 저장
      const scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add("modal-open");
    } else {
      // 모달이 닫힐 때 스크롤 위치 복원
      const scrollY = parseInt(document.body.style.top || "0", 10);
      document.body.classList.remove("modal-open");
      document.body.style.top = "";
      window.scrollTo(0, -scrollY);
    }

    // 컴포넌트 언마운트 시 정리
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.top = "";
    };
  }, [showPasswordModal, showErrorReportModal]);

  if (!user) {
    return <div>로딩 중...</div>;
  }

  return (
    <div className="dashboard-container">
      {/* 상단 헤더 */}
      <header className="dashboard-header">
        <div className="header-logo">ClassTrans</div>
        <div className="header-user">
          <span>
            {user?.school} {user?.username} 님
          </span>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        <Sidebar items={sidebarItems} activeItem="profile" user={user} />
        <main className="profile-content">
          <div className="profile-header">
            <h1>프로필</h1>
          </div>
          <div className="profile-card">
            <div className="profile-detail">
              <div className="profile-field">
                <div className="field-label">학교</div>
                <div className="field-value">{user.school || "정보 없음"}</div>
              </div>
              <div className="profile-field">
                <div className="field-label">아이디</div>
                <div className="field-value">{user.username}</div>
              </div>
              <div className="profile-field">
                <div className="field-label">비밀번호</div>
                <div className="field-value">
                  <button
                    className="password-change-button"
                    onClick={() => setShowPasswordModal(true)}
                  >
                    비밀번호 변경
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {showPasswordModal && <PasswordChangeModal />}
      {showErrorReportModal && <ErrorReportModal />}
    </div>
  );
};

export default Profile;
